import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/api/client";

interface ShopeeOrderRow {
  shopee_order_line_id: number;
  shopee_order_id: string;
  shopee_order_at: string | null;
  jan: string | null;
  shopee_item_name: string | null;
  quantity: number;
  shop: {
    connection_id: number;
    country_code: string | null;
    shop_id: string | null;
    shop_name: string | null;
  };
  product?: {
    id: number;
    jan: string;
    product_name: string;
    unit_price: number;
    tax_rate: number | null;
    order_unit: number;
    public_stock: number;
    tier: number;
  };
  // out_of_stock のみ
  available_quantity?: number;
  shortfall?: number;
}

interface PendingResponse {
  in_stock: ShopeeOrderRow[];
  out_of_stock: ShopeeOrderRow[];
  out_of_catalog: ShopeeOrderRow[];
  totals: {
    in_stock_lines: number;
    out_of_stock_lines: number;
    out_of_catalog_lines: number;
    estimated_amount_in_stock: number;
  };
}

interface SucceededEntry {
  shopee_order_id: string;
  shopee_order_line_ids: number[];
  order_id: number;
  order_number: string;
  total_amount: number;
}

interface FailedEntry {
  shopee_order_id: string;
  shopee_order_line_ids: number[];
  error: { code: string; message: string };
}

interface ConfirmResponse {
  result: "ok" | "partial" | "all_failed";
  succeeded: SucceededEntry[];
  failed: FailedEntry[];
  totals: { succeeded_orders: number; failed_orders: number };
}

function formatYen(n: number): string {
  return new Intl.NumberFormat("ja-JP", {
    style: "currency",
    currency: "JPY",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatShop(row: ShopeeOrderRow): string {
  const { country_code, shop_name, shop_id } = row.shop;
  const head = shop_name || shop_id || "Shopee";
  return country_code ? `${head} (${country_code})` : head;
}

/**
 * 顧客側: 朝の取込結果確認画面 (本実装)。
 * 設計 §3 のイメージ図に沿って 3 カテゴリ表示 + 1 クリック確定。
 */
export function ShopeeConfirmPage() {
  const [data, setData] = useState<PendingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 在庫不足行の数量編集 (line_id → quantity)
  const [oosQuantities, setOosQuantities] = useState<Record<number, number>>({});
  // 在庫不足行のスキップ選択
  const [oosSkipped, setOosSkipped] = useState<Record<number, boolean>>({});
  // 在庫 OK 行の数量編集 (デフォルトは Shopee 受注数で全量発注)
  const [inStockQuantities, setInStockQuantities] = useState<Record<number, number>>({});
  // 在庫 OK 行のスキップ選択 (スキップした行は翌朝の取込で再表示)
  const [inStockSkipped, setInStockSkipped] = useState<Record<number, boolean>>({});

  const [submitting, setSubmitting] = useState(false);
  const [confirmResult, setConfirmResult] = useState<ConfirmResponse | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return api
      .get<PendingResponse>("/account/shopee/pending-orders")
      .then((res) => {
        setData(res.data);
        // 在庫不足の初期値: available_quantity (= 取れる範囲で全量発注を提案)
        const initial: Record<number, number> = {};
        res.data.out_of_stock.forEach((r) => {
          initial[r.shopee_order_line_id] = r.available_quantity ?? 0;
        });
        setOosQuantities(initial);
        setOosSkipped({});
        // 在庫 OK の初期値: Shopee 受注数 (現行挙動と同じ = 全量発注)
        const initialInStock: Record<number, number> = {};
        res.data.in_stock.forEach((r) => {
          initialInStock[r.shopee_order_line_id] = r.quantity;
        });
        setInStockQuantities(initialInStock);
        setInStockSkipped({});
      })
      .catch((e) => setError(apiErrorMessage(e, "取込結果の取得に失敗しました")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 確定対象 = スキップしていない & 数量 > 0 の行 (在庫 OK / 在庫不足 両方)
  const selectedItems = useMemo(() => {
    if (!data) return [];
    const items: { shopee_order_line_id: number; quantity?: number }[] = [];
    data.in_stock.forEach((r) => {
      const lineId = r.shopee_order_line_id;
      if (inStockSkipped[lineId]) return;
      const q = inStockQuantities[lineId] ?? r.quantity;
      if (q <= 0) return;
      // 満額発注なら quantity 省略 (サーバ側で ShopeeOrder.quantity を使用)
      if (q === r.quantity) {
        items.push({ shopee_order_line_id: lineId });
      } else {
        items.push({ shopee_order_line_id: lineId, quantity: q });
      }
    });
    data.out_of_stock.forEach((r) => {
      const lineId = r.shopee_order_line_id;
      if (oosSkipped[lineId]) return;
      const q = oosQuantities[lineId] ?? 0;
      if (q > 0) {
        items.push({ shopee_order_line_id: lineId, quantity: q });
      }
    });
    return items;
  }, [data, oosQuantities, oosSkipped, inStockQuantities, inStockSkipped]);

  const estimatedTotal = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    // 在庫 OK 分 (スキップ・数量減算を反映、税抜単価 × 数量 × (1 + 税率))
    data.in_stock.forEach((r) => {
      const lineId = r.shopee_order_line_id;
      if (inStockSkipped[lineId]) return;
      const q = inStockQuantities[lineId] ?? r.quantity;
      if (q > 0 && r.product) {
        const rate = r.product.tax_rate ?? 0.1;
        total += Math.round(r.product.unit_price * q * (1 + rate));
      }
    });
    // 在庫不足からの部分発注分
    data.out_of_stock.forEach((r) => {
      const lineId = r.shopee_order_line_id;
      if (oosSkipped[lineId]) return;
      const q = oosQuantities[lineId] ?? 0;
      if (q > 0 && r.product) {
        const rate = r.product.tax_rate ?? 0.1;
        total += Math.round(r.product.unit_price * q * (1 + rate));
      }
    });
    return total;
  }, [data, oosQuantities, oosSkipped, inStockQuantities, inStockSkipped]);

  const handleConfirm = async () => {
    if (selectedItems.length === 0) {
      setError("確定対象の注文がありません");
      return;
    }
    if (
      !window.confirm(
        `${selectedItems.length} 件の Shopee 受注を発注確定します。\n` +
          `予想合計: ${formatYen(estimatedTotal)}\n\n` +
          `登録済の決済カードで自動課金されます。よろしいですか？`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    setConfirmResult(null);
    try {
      const res = await api.post<ConfirmResponse>(
        "/account/shopee/pending-orders/confirm",
        { items: selectedItems },
      );
      setConfirmResult(res.data);
      // 取込結果を再読み込み (確定済の行が消える)
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "確定処理に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">🔄 Shopee 取込結果の確認</h1>
        <p className="text-sm text-gray-500">読み込み中…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">🔄 Shopee 取込結果の確認</h1>
        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    );
  }

  const t = data.totals;
  const hasAnything =
    t.in_stock_lines + t.out_of_stock_lines + t.out_of_catalog_lines > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">🔄 Shopee 取込結果の確認</h1>
          <p className="text-sm text-gray-600 mt-1">
            毎朝 Shopee から受注を取り込みました。「全件確定して発注」を押すと、
            在庫 OK の商品と在庫不足の部分発注分を一括で発注します。Stripe に登録済のカードで自動課金されます。
          </p>
        </div>
        <nav className="flex gap-2 text-sm flex-shrink-0">
          <Link to="/account/shopee/connections" className="btn-secondary text-xs">
            店舗連携
          </Link>
          <Link to="/account/shopee/sync-settings" className="btn-secondary text-xs">
            在庫同期設定
          </Link>
        </nav>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {confirmResult && (
        <div
          className={
            "rounded-md border px-4 py-3 text-sm space-y-2 " +
            (confirmResult.result === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : confirmResult.result === "partial"
              ? "bg-yellow-50 border-yellow-200 text-yellow-800"
              : "bg-red-50 border-red-200 text-red-800")
          }
        >
          <div className="font-semibold">
            確定結果: 成功 {confirmResult.totals.succeeded_orders} 件 / 失敗{" "}
            {confirmResult.totals.failed_orders} 件
          </div>
          {confirmResult.succeeded.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {confirmResult.succeeded.map((s) => (
                <li key={s.shopee_order_id}>
                  Shopee {s.shopee_order_id} → 発注 {s.order_number}（
                  {formatYen(s.total_amount)}）
                </li>
              ))}
            </ul>
          )}
          {confirmResult.failed.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {confirmResult.failed.map((f) => (
                <li key={f.shopee_order_id}>
                  Shopee {f.shopee_order_id} 失敗: {f.error.message}（{f.error.code}）
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!hasAnything ? (
        <div className="card p-6 text-center text-gray-500">
          確認待ちの注文はありません。次の取込まで自由に作業を進めてください。
        </div>
      ) : (
        <>
          {/* ---- ✅ そのまま発注できる ---- */}
          <section className="card p-4">
            <h2 className="font-semibold mb-2">
              ✅ そのまま発注できる ({t.in_stock_lines} 件 / 最大{" "}
              {formatYen(t.estimated_amount_in_stock)} (満額発注時))
            </h2>
            {data.in_stock.length === 0 ? (
              <p className="text-sm text-gray-500">該当なし</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  デフォルトは Shopee 受注数の満額発注です。数量を減らしたり行ごとにスキップもできます。スキップした行は翌朝の取込でまた表示されます。
                </p>
                <ul className="divide-y text-sm">
                  {data.in_stock.map((r) => {
                    const lineId = r.shopee_order_line_id;
                    const skipped = !!inStockSkipped[lineId];
                    const q = inStockQuantities[lineId] ?? r.quantity;
                    return (
                      <li key={lineId} className="py-2 space-y-1">
                        <div>
                          <div className="font-medium">
                            {r.product?.product_name || r.shopee_item_name}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            JAN {r.jan} × Shopee 受注 {r.quantity} 個 / 単価{" "}
                            {formatYen(r.product?.unit_price ?? 0)} (税抜)
                          </div>
                          <div className="text-xs text-gray-400">
                            Shopee 注文 {r.shopee_order_id} / {formatShop(r)}
                          </div>
                        </div>
                        <div className="flex gap-4 items-center text-xs">
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`in-${lineId}`}
                              checked={!skipped}
                              onChange={() =>
                                setInStockSkipped((s) => ({ ...s, [lineId]: false }))
                              }
                            />
                            <span>
                              <input
                                type="number"
                                min={1}
                                max={r.quantity}
                                value={q}
                                onChange={(e) =>
                                  setInStockQuantities((s) => ({
                                    ...s,
                                    [lineId]: Math.max(
                                      1,
                                      Math.min(r.quantity, Number(e.target.value) || 1),
                                    ),
                                  }))
                                }
                                disabled={skipped}
                                className="input w-20 inline-block mx-1"
                              />{" "}
                              個発注 (上限 {r.quantity})
                            </span>
                          </label>
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`in-${lineId}`}
                              checked={skipped}
                              onChange={() =>
                                setInStockSkipped((s) => ({ ...s, [lineId]: true }))
                              }
                            />
                            スキップ
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>

          {/* ---- ⚠️ 在庫不足 ---- */}
          {data.out_of_stock.length > 0 && (
            <section className="card p-4">
              <h2 className="font-semibold mb-2">
                ⚠️ 在庫不足 ({t.out_of_stock_lines} 件)
              </h2>
              <p className="text-xs text-gray-500 mb-2">
                利用可能な在庫数までなら部分発注できます。スキップすると Wonect に共有されます ✓
              </p>
              <ul className="divide-y text-sm">
                {data.out_of_stock.map((r) => {
                  const lineId = r.shopee_order_line_id;
                  const available = r.available_quantity ?? 0;
                  const skipped = !!oosSkipped[lineId];
                  const q = oosQuantities[lineId] ?? 0;
                  return (
                    <li key={lineId} className="py-2 space-y-1">
                      <div className="flex justify-between">
                        <div>
                          <div className="font-medium">
                            {r.product?.product_name || r.shopee_item_name}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            JAN {r.jan} × 必要 {r.quantity} 個 / 利用可 {available} 個 (不足{" "}
                            {r.shortfall})
                          </div>
                          <div className="text-xs text-gray-400">
                            Shopee 注文 {r.shopee_order_id} / {formatShop(r)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-4 items-center text-xs">
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="radio"
                            name={`oos-${lineId}`}
                            checked={!skipped}
                            onChange={() =>
                              setOosSkipped((s) => ({ ...s, [lineId]: false }))
                            }
                          />
                          <span>
                            <input
                              type="number"
                              min={0}
                              max={available}
                              value={q}
                              onChange={(e) =>
                                setOosQuantities((s) => ({
                                  ...s,
                                  [lineId]: Math.max(
                                    0,
                                    Math.min(available, Number(e.target.value) || 0),
                                  ),
                                }))
                              }
                              disabled={skipped}
                              className="input w-20 inline-block mx-1"
                            />{" "}
                            個だけ発注
                          </span>
                        </label>
                        <label className="inline-flex items-center gap-1">
                          <input
                            type="radio"
                            name={`oos-${lineId}`}
                            checked={skipped}
                            onChange={() =>
                              setOosSkipped((s) => ({ ...s, [lineId]: true }))
                            }
                          />
                          スキップ
                        </label>
                        <span className="badge badge-blue">Wonect に共有済 ✓</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* ---- ⚠️ 取扱外 ---- */}
          {data.out_of_catalog.length > 0 && (
            <section className="card p-4">
              <h2 className="font-semibold mb-2">
                ⚠️ 取扱外商品 ({t.out_of_catalog_lines} 件)
              </h2>
              <p className="text-xs text-gray-500 mb-2">
                Wonect の商品マスタにない商品です。発注対象には含まれませんが、
                売上情報は需要ヒートマップとして Wonect に共有されます ✓
              </p>
              <ul className="divide-y text-sm">
                {data.out_of_catalog.map((r) => (
                  <li key={r.shopee_order_line_id} className="py-2">
                    <div className="font-mono text-xs">
                      JAN {r.jan || "(なし)"} × {r.quantity} 個
                    </div>
                    <div className="text-xs text-gray-500">
                      {r.shopee_item_name}
                    </div>
                    <div className="text-xs text-gray-400">
                      Shopee 注文 {r.shopee_order_id} / {formatShop(r)}{" "}
                      <span className="badge badge-blue ml-1">Wonect に共有済 ✓</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ---- 確定ボタン ---- */}
          <div className="card p-4 bg-gray-50 sticky bottom-0">
            <div className="flex justify-between items-center gap-4">
              <div className="text-sm">
                <div>
                  確定対象: <strong>{selectedItems.length} 件</strong>
                </div>
                <div className="text-xs text-gray-600">
                  予想合計 (税込): {formatYen(estimatedTotal)}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleConfirm}
                  disabled={submitting || selectedItems.length === 0}
                  className="btn-primary"
                >
                  {submitting
                    ? "確定中…"
                    : `確定して発注 (${selectedItems.length} 件)`}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
